import React, { useMemo, useCallback } from "react";
import { Input } from "reactstrap";

/**
 * Dynamic inspection entry grid — one input cell per piece being checked.
 *
 * Legacy parity: Inspect.vb dynamically creates TextBox controls inside
 * Panel1 (one per piece). Labels are double-clickable to open the per-cell
 * Supervisor Override dialog. Out-of-spec cells get visually highlighted.
 *
 * Modern UI: small consistent CSS classes (defined inline via a single
 * <style> block at the bottom of this file) replace the old inline styles
 * so the cell appearance matches the rest of the dashboard's card / input
 * tokens. Functional API unchanged — same props.
 */
const DynamicCommentGrid = ({
  count,
  extraEntries = [],
  values,
  onChange,
  labels,
  names,
  disabledFields = [],
  highlightedFields = [],
  overrideEnabled = false,
  onLabelDoubleClick,
  onFieldBlur,
  onRemoveBox,
  onDeleteSaved,
}) => {
  const baseBoxes = useMemo(
    () => Array.from({ length: count }, (_, i) => i),
    [count]
  );

  const disabledSet = useMemo(
    () => new Set(disabledFields || []),
    [disabledFields]
  );
  const highlightedSet = useMemo(
    () => new Set(highlightedFields || []),
    [highlightedFields]
  );

  const handleInputChange = useCallback(
    (name, value) => {
      onChange?.(name, value);
    },
    [onChange]
  );

  const handleLabelDoubleClick = useCallback(
    (name, label) => {
      onLabelDoubleClick?.(name, label);
    },
    [onLabelDoubleClick]
  );

  const handleBlur = useCallback(
    (name) => {
      onFieldBlur?.(name);
    },
    [onFieldBlur]
  );

  return (
    <div className="di-entry-grid">
      {baseBoxes.map((i) => {
        const name = names?.[i] || `txtDynamic${i + 1}`;
        const label = labels?.[i] || `+${i + 1}`;
        const isDisabled = disabledSet.has(name);
        const isHighlighted = highlightedSet.has(name);

        return (
          <div
            key={name}
            className={[
              "di-entry-cell",
              overrideEnabled ? "di-entry-cell--ovr" : "",
              isHighlighted ? "di-entry-cell--out" : "",
              isDisabled ? "di-entry-cell--disabled" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onDoubleClick={
              overrideEnabled
                ? () => handleLabelDoubleClick(name, label)
                : undefined
            }
            title={
              overrideEnabled
                ? "Double-click anywhere on this box to open Supervisor Override"
                : undefined
            }
          >
            <div className="d-flex align-items-baseline justify-content-between mb-1">
              <div
                className={
                  "di-entry-label" + (overrideEnabled ? " di-entry-label--ovr" : "")
                }
                onDoubleClick={() => handleLabelDoubleClick(name, label)}
                title={
                  overrideEnabled
                    ? "Double-click to open Supervisor Override for this piece"
                    : undefined
                }
              >
                {label}
              </div>
              <span className="d-inline-flex align-items-center" style={{ gap: 4 }}>
                {isHighlighted ? (
                  // Tester request (2026-10-01): a reading out of tolerance says "Failed", not "OUT".
                  <span className="di-entry-out-tag">Failed</span>
                ) : null}
                {isDisabled && overrideEnabled && onDeleteSaved ? (
                  // Saved base piece + supervisor: delete the DB row.
                  <button
                    type="button"
                    onClick={() => onDeleteSaved(name, label)}
                    title="Delete this SAVED value from the database (supervisor)"
                    aria-label="Delete saved value"
                    style={{
                      border: 0,
                      background: "transparent",
                      color: "#b02a37",
                      cursor: "pointer",
                      lineHeight: 1,
                      padding: 0,
                      fontSize: "0.85rem",
                    }}
                  >
                    <i className="ri-delete-bin-6-line" />
                  </button>
                ) : isDisabled ? (
                  <i
                    className="ri-lock-2-line"
                    title="Saved — a supervisor is required to change or delete this"
                    style={{ color: "#9aa0a6", fontSize: "0.78rem" }}
                  />
                ) : null}
              </span>
            </div>

            <Input
              id={name}
              type="text"
              bsSize="sm"
              value={values[name] || ""}
              onChange={(e) => handleInputChange(name, e.target.value)}
              onBlur={() => handleBlur(name)}
              disabled={isDisabled}
            />
          </div>
        );
      })}

      {extraEntries.map((entry) => {
        const name = entry?.name;
        if (!name) return null;

        const label = entry?.label;
        const isDisabled = disabledSet.has(name);
        const isHighlighted = highlightedSet.has(name);

        return (
          <div
            key={name}
            className={[
              "di-entry-cell",
              "di-entry-cell--added",
              overrideEnabled ? "di-entry-cell--ovr" : "",
              isHighlighted ? "di-entry-cell--out" : "",
              isDisabled ? "di-entry-cell--disabled" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onDoubleClick={
              overrideEnabled
                ? () => handleLabelDoubleClick(name, label)
                : undefined
            }
            title={
              overrideEnabled
                ? "Double-click anywhere on this box to open Supervisor Override"
                : undefined
            }
          >
            <div className="d-flex align-items-baseline justify-content-between mb-1">
              <div
                className={
                  "di-entry-label" + (overrideEnabled ? " di-entry-label--ovr" : "")
                }
                onDoubleClick={() => handleLabelDoubleClick(name, label)}
                title={
                  overrideEnabled
                    ? "Double-click to open Supervisor Override for this piece"
                    : undefined
                }
              >
                {label}
              </div>
              <span className="d-inline-flex align-items-center" style={{ gap: 4 }}>
                <span className="di-entry-added-tag">ADDED</span>
                {onRemoveBox && !isDisabled ? (
                  // Unsaved box: anyone may remove (frontend only).
                  <button
                    type="button"
                    onClick={() => onRemoveBox(name)}
                    title="Remove this box (unsaved)"
                    aria-label="Remove this box"
                    style={{
                      border: 0,
                      background: "transparent",
                      color: "#dc3545",
                      cursor: "pointer",
                      lineHeight: 1,
                      padding: 0,
                      fontSize: "0.85rem",
                    }}
                  >
                    <i className="ri-close-circle-line" />
                  </button>
                ) : isDisabled && overrideEnabled && onDeleteSaved ? (
                  // Saved box + supervisor: delete the row from the database.
                  <button
                    type="button"
                    onClick={() => onDeleteSaved(name, label)}
                    title="Delete this SAVED value from the database (supervisor)"
                    aria-label="Delete saved value"
                    style={{
                      border: 0,
                      background: "transparent",
                      color: "#b02a37",
                      cursor: "pointer",
                      lineHeight: 1,
                      padding: 0,
                      fontSize: "0.85rem",
                    }}
                  >
                    <i className="ri-delete-bin-6-line" />
                  </button>
                ) : isDisabled ? (
                  // Saved box + normal user: locked.
                  <i
                    className="ri-lock-2-line"
                    title="Saved — a supervisor is required to change or delete this"
                    style={{ color: "#9aa0a6", fontSize: "0.78rem" }}
                  />
                ) : null}
              </span>
            </div>

            <Input
              id={name}
              type="text"
              bsSize="sm"
              value={values[name] || ""}
              onChange={(e) => handleInputChange(name, e.target.value)}
              onBlur={() => handleBlur(name)}
              disabled={isDisabled}
            />
          </div>
        );
      })}

      <style>{`
        /* Cleanup pass: grid/cell sizing now lives ONLY in di-theme.css
           (single source of truth). These rules are the override-affordance
           styles unique to this component. */
        .di-module .di-entry-cell--ovr { cursor: help; }
        .di-module .di-entry-label--ovr {
          cursor: help;
          text-decoration: underline dotted #f0ad4e;
          text-underline-offset: 3px;
        }
        .di-module .di-entry-label--ovr:hover {
          color: #b8860b;
        }
        .di-module .di-entry-out-tag {
          font-size: 0.6rem;
          color: var(--di-fail);
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default React.memo(DynamicCommentGrid);
